import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { generateContent } from '../services/geminiService';
import { systemPrompt, tools, dispatchAction } from '../services/aiActions';
import { matchOfflineIntent } from '../services/offlineIntentMatcher';
import { DEFAULT_MODEL_ID, MODEL_STORAGE_KEY, VOICE_OUTPUT_STORAGE_KEY } from '../config/aiModels';
import { useTheme } from './ThemeContext';
import { useLogsModal } from './LogsModalContext';
import { useAppConfigModal } from './AppConfigModalContext';

const ChatContext = createContext(null);

let nextMessageId = 1;

function speak(text) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new window.SpeechSynthesisUtterance(text);
  window.speechSynthesis.speak(utterance);
}

export function ChatProvider({ children }) {
  const navigate = useNavigate();
  const { toggle: toggleTheme } = useTheme();
  const { open: openLogs } = useLogsModal();
  const { open: openAppConfig } = useAppConfigModal();
  const queryClient = useQueryClient();
  const invalidateAppConfig = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['app-config'] }),
    [queryClient],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [modelId, setModelIdState] = useState(
    () => localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL_ID
  );
  const [voiceEnabled, setVoiceEnabledState] = useState(
    () => localStorage.getItem(VOICE_OUTPUT_STORAGE_KEY) === 'true'
  );

  const historyRef = useRef([]);

  const setModelId = useCallback((id) => {
    setModelIdState(id);
    localStorage.setItem(MODEL_STORAGE_KEY, id);
  }, []);

  const setVoiceEnabled = useCallback((enabled) => {
    setVoiceEnabledState(enabled);
    localStorage.setItem(VOICE_OUTPUT_STORAGE_KEY, String(enabled));
  }, []);

  const pushMessage = useCallback((role, text) => {
    setMessages((prev) => [...prev, { id: nextMessageId++, role, text }]);
    if (role === 'assistant' && voiceEnabled) speak(text);
  }, [voiceEnabled]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;

    pushMessage('user', trimmed);
    historyRef.current = [...historyRef.current, { role: 'user', parts: [{ text: trimmed }] }];
    setIsThinking(true);

    try {
      let result = await generateContent({ modelId, systemPrompt, tools, history: historyRef.current });
      let actionResult = null;

      // AI unreachable (expired/missing key, network error, rate-limited) —
      // fall back to a keyword-only offline matcher that can still execute
      // the handful of deterministic actions (navigate, toggle theme, open
      // server logs) without needing a model call at all.
      if (result.type === 'error') {
        const offlineIntent = matchOfflineIntent(trimmed);
        if (offlineIntent) {
          actionResult = await dispatchAction(offlineIntent.name, offlineIntent.args, { navigate, toggleTheme, openLogs, openAppConfig, invalidateAppConfig });
          pushMessage(
            'assistant',
            actionResult.status === 'executed'
              ? `AI assistant is unavailable right now, but I recognized this as something I can still do offline: ${actionResult.detail}`
              : result.message,
          );
          return;
        }
        pushMessage('assistant', result.message);
        return;
      }

      if (result.type === 'functionCall') {
        historyRef.current = [...historyRef.current, result.modelPart];

        actionResult = await dispatchAction(result.name, result.args, { navigate, toggleTheme, openLogs, openAppConfig, invalidateAppConfig });

        historyRef.current = [
          ...historyRef.current,
          { role: 'user', parts: [{ functionResponse: { name: result.name, response: actionResult } }] },
        ];

        result = await generateContent({ modelId, systemPrompt, tools, history: historyRef.current });
      }

      if (result.type === 'text') {
        historyRef.current = [...historyRef.current, { role: 'model', parts: [{ text: result.text }] }];
        pushMessage('assistant', result.text);
      } else if (result.type === 'error') {
        // The action itself already ran above — the AI only failed to
        // narrate it, so surface what actually happened instead of an error.
        pushMessage('assistant', actionResult?.status === 'executed' ? actionResult.detail : result.message);
      } else {
        pushMessage('assistant', "Done. Let me know if you'd like anything else.");
      }
    } finally {
      setIsThinking(false);
    }
  }, [modelId, isThinking, navigate, toggleTheme, openLogs, openAppConfig, invalidateAppConfig, pushMessage]);

  const value = {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggleOpen: () => setIsOpen((o) => !o),
    messages,
    isThinking,
    sendMessage,
    modelId,
    setModelId,
    voiceEnabled,
    setVoiceEnabled,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useChat = () => useContext(ChatContext);
